import type { ReactElement } from "react";
import { cloneElement } from "react";
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from "next";
import CompanyPageLayout from "components/company/company-page-layout/company-page-layout";
import { getFirst } from "utils/array";
import fetcher from "utils/fetcher";
import type { CompanyInitialDataResponse } from "types/api/company";
import ContactNoCompanyUnpublished from "../company-contacts/contact-no-company-unpublished";

export interface CompanyPageProps
  extends InferGetStaticPropsType<typeof getStaticProps> {
  children: ReactElement;
}

const CompanyPage = ({
  children,
  company,
  companySlug,
  errorMessage,
  errorCode,
  hash,
}: CompanyPageProps) => {
  if (errorMessage === "Unpublished" || errorCode || !companySlug) {
    return <ContactNoCompanyUnpublished data={hash} />;
  }

  const isInternational = company.headquartersCountryName !== "United States";
  const isPublicCompany = company.classification.toLowerCase() === "public";

  if (company) {
    return (
      <CompanyPageLayout
        company={company}
        slug={companySlug}
        isInternational={isInternational}
      >
        {cloneElement(children, {
          companySlug,
          isInternational,
          isPublicCompany,
          tags: company.companyTags,
        })}
      </CompanyPageLayout>
    );
  }

  return null;
};

interface CompanyStaticProps {
  company?: CompanyInitialDataResponse;
  companySlug?: string;
  errorCode?: number;
  errorMessage?: string;
  hash?: string;
}

export const getStaticProps: GetStaticProps<CompanyStaticProps> = async ({
  params,
}) => {
  const companySlug = getFirst(params.slug);

  try {
    if (!companySlug) {
      throw new Error("no company slug provided");
    }

    const company = await fetcher<CompanyInitialDataResponse | Response>(
      `/system/company/${companySlug}`
    );

    if ((company as Response).status === 204) {
      throw new Error("Unpublished");
    }

    return {
      props: {
        company: company as CompanyInitialDataResponse,
        companySlug,
      },
      revalidate: 180,
    };
  } catch (error) {
    return {
      props: { errorCode: 404, errorMessage: error.message },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: "blocking",
});

export default CompanyPage;
